import React, { useEffect, useRef, useState } from "react";
import api, { BOT_LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2, Mail, Phone } from "lucide-react";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function useSessionId(slug) {
  const key = `nia_session_${slug}`;
  const [id] = useState(() => {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const nid = uid();
    localStorage.setItem(key, nid);
    return nid;
  });
  return id;
}

export default function ChatWidget({ slug, previewMode = false }) {
  const [open, setOpen] = useState(previewMode);
  const [sector, setSector] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef();
  const sessionId = useSessionId(slug);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/chat/${slug}/preview-info`)
      .then(({ data }) => setSector(data))
      .catch((e) => setError(e?.response?.data?.detail || "Setor não encontrado."));
    if (!previewMode) {
      api.get(`/chat/${slug}/history`, { params: { session_id: sessionId } })
        .then(({ data }) => setMessages(data.messages || []))
        .catch(() => {});
    }
  }, [slug]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  async function send(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const { data } = await api.post(`/chat/${slug}/message`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erro ao enviar mensagem.";
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${detail}` }]);
    } finally { setSending(false); }
  }

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <motion.button
          data-testid="chat-widget-trigger"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full overflow-hidden nia-bubble-pulse focus:outline-none focus:ring-4 focus:ring-[#1e5a8e]/30"
          aria-label="Abrir chat"
        >
          <img src={BOT_LOGO_URL} alt="Chat" className="w-full h-full object-cover" />
        </motion.button>
      )}

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            role="dialog"
            aria-label="Chatbot"
            className="fixed bottom-6 right-6 z-50 w-[380px] sm:w-[400px] h-[600px] max-h-[85vh] flex flex-col shadow-2xl rounded-2xl overflow-hidden bg-white border border-slate-200"
          >
            <header
              className="px-4 py-3 flex items-center gap-3 text-white"
              style={{ background: "linear-gradient(135deg, #1e5a8e 0%, #2d82c4 60%, #40c4d0 100%)" }}
            >
              <div className="w-10 h-10 rounded-full bg-white/20 overflow-hidden shrink-0 flex items-center justify-center">
                {sector?.logo_url ? (
                  <img src={sector.logo_url} alt={sector.name} className="w-full h-full object-cover" />
                ) : (
                  <img src={BOT_LOGO_URL} alt="bot" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-[Outfit] font-semibold leading-tight truncate">
                  {sector?.name || "Chatbot"}
                </p>
                <p className="text-[11px] text-white/80 leading-tight">
                  {sector?.active !== false ? "Online" : "Indisponível"}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center"
                aria-label="Fechar"
                data-testid="chat-widget-close"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" aria-live="polite">
              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}
              {!error && messages.length === 0 && (
                <div className="text-center pt-8">
                  <img src={BOT_LOGO_URL} alt="" className="w-20 h-20 mx-auto mb-3" />
                  <p className="font-[Outfit] font-semibold text-slate-800">Olá! Como posso ajudar?</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-[280px] mx-auto">
                    Faça perguntas sobre {sector?.name?.toLowerCase() || "este setor"}. Se eu não souber,
                    passo o contato humano.
                  </p>
                  {sector && (
                    <div className="mt-4 text-xs text-slate-400 flex flex-col items-center gap-1">
                      {sector.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {sector.contact_email}</span>}
                      {sector.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {sector.contact_phone}</span>}
                    </div>
                  )}
                </div>
              )}
              {messages.map((m, i) => (
                <MessageBubble key={i} role={m.role} text={m.text} />
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-xs text-slate-500 pl-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> pensando…
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={send} className="border-t border-slate-200 p-3 bg-white flex gap-2">
              <Input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Digite sua pergunta…"
                disabled={sending || !sector}
                className="flex-1"
              />
              <Button
                data-testid="chat-send"
                type="submit"
                disabled={sending || !input.trim() || !sector}
                className="bg-[#1e5a8e] hover:bg-[#164570]"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MessageBubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={
          isUser
            ? "bg-slate-100 text-slate-900 rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap"
            : "bg-[#e6f0fa] text-[#0f2f4d] rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[85%] text-sm leading-relaxed border border-[#cce0f5] whitespace-pre-wrap"
        }
      >
        {formatBold(text)}
      </div>
    </div>
  );
}

function formatBold(text) {
  // Very light markdown: **bold**
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}
